// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { faLightbulb, faWifi } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DmxStatus } from "./platformLiveProtocol";

import styles from "./LiveConnectionIndicators.module.scss";

const joinClassNames = (...classNames: Array<string | false | null | undefined>): string => {
  return classNames.filter((className): className is string => typeof className === "string").join(" ");
};

type LiveStatus = "off" | "connecting" | "connected" | "rejected" | "disconnected" | "reconnecting";

export interface LiveConnectionIndicatorsProps {
  liveStatus: LiveStatus;
  liveError?: string | null;
  dmxStatus: DmxStatus;
  onReconnect?: () => void;
  compact?: boolean;
  theme?: "dark" | "light";
}

const getLiveStatusClass = (liveStatus: LiveStatus): string => {
  if (liveStatus === "connected") {
    return styles.signalGood;
  }
  if (liveStatus === "connecting" || liveStatus === "reconnecting") {
    return styles.signalPending;
  }
  if (liveStatus === "off") {
    return styles.signalOff;
  }
  return styles.signalBad;
};

const getDmxStatusClass = (dmxStatus: DmxStatus): string => {
  if (dmxStatus === "ready") {
    return styles.signalGood;
  }
  if (dmxStatus === "degraded") {
    return styles.signalPending;
  }
  return styles.signalOff;
};

const getLiveTitle = (liveStatus: LiveStatus, liveError: string | null | undefined): string => {
  const base = `Live: ${liveStatus}`;
  return liveError === null || liveError === undefined || liveError.length === 0 ? base : `${base} (${liveError})`;
};

const getDmxTitle = (dmxStatus: DmxStatus): string => `DMX: ${dmxStatus}`;

const LiveConnectionIndicators = ({
  liveStatus,
  liveError = null,
  dmxStatus,
  onReconnect,
  compact = false,
  theme = "dark",
}: LiveConnectionIndicatorsProps): React.ReactElement => {
  const showReconnect =
    liveStatus === "reconnecting" || liveStatus === "disconnected" || liveStatus === "rejected";

  return (
    <div className={joinClassNames(styles.signalRow, compact && styles.signalRowCompact, theme === "light" && styles.signalRowLight)}>
      <div
        className={joinClassNames(styles.signalChip, getLiveStatusClass(liveStatus))}
        aria-label="Live Judge Voting Status"
        title={getLiveTitle(liveStatus, liveError)}
      >
        <FontAwesomeIcon icon={faWifi} className={styles.signalIcon} aria-hidden="true" />
        <span className={styles.signalDot} aria-hidden="true" />
        <span className={styles.signalSrOnly}>{getLiveTitle(liveStatus, liveError)}</span>
      </div>
      <div
        className={joinClassNames(styles.signalChip, getDmxStatusClass(dmxStatus))}
        aria-label="DMX Interface Status"
        title={getDmxTitle(dmxStatus)}
      >
        <FontAwesomeIcon icon={faLightbulb} className={styles.signalIcon} aria-hidden="true" />
        <span className={styles.signalDot} aria-hidden="true" />
        <span className={styles.signalSrOnly}>{getDmxTitle(dmxStatus)}</span>
      </div>
      {showReconnect && onReconnect !== undefined ? (
        <button type="button" className={styles.reconnectButton} onClick={onReconnect}>
          Reconectar
        </button>
      ) : null}
    </div>
  );
};

export default LiveConnectionIndicators;
